/**
 * Frontend-side message types — mirror the backend ingest payload.
 *
 * In Phase 1 the backend already parses IRC into structured events, so the
 * client only needs the type shape — no IRC parsing here.
 */


export interface TwitchMessage {
  type: 'message';
  source: 'twitch';
  externalId: string;
  channel: string;
  userId: string;
  username: string;
  displayName: string;
  color: string;
  text: string;
  badges: Array<{ id: string; version: string }>;
  emotes: Record<string, string[]>;
  isAction: boolean;
  isHighlighted?: boolean;
  replyParentMsgId: string | null;
  replyParentMeta?: { userLogin: string; displayName: string; msgBody: string } | null;
  customRewardId?: string | null;
  receivedAt: number;
  /** Set when loaded from history and the message was deleted/cleared. */
  isDeleted?: boolean;
}

export interface YouTubeMessage {
  type: 'message' | 'superChat' | 'newSponsor';
  source: 'youtube';
  externalId: string;
  channel: string;
  userId: string;
  username: string;
  displayName: string;
  color: string;
  text: string;
  badges: Array<{ id: string; version: string }>;
  emotes: Record<string, string[]>;
  isAction: boolean;
  replyParentMsgId: string | null;
  receivedAt: number;
  /** Set when loaded from history and the message was deleted/cleared. */
  isDeleted?: boolean;
  /** SuperChat: amount in the smallest currency unit (e.g. cents), null otherwise. */
  superChatAmount?: number | null;
  /** SuperChat: ISO 4217 currency code, e.g. "EUR". */
  superChatCurrency?: string | null;
}

/** Union of all chat message variants. */
export type ChatMessage = TwitchMessage | YouTubeMessage;

export interface TwitchChatEvent {
  type: 'notice' | 'delete' | 'clearchat' | 'unban';
  source: 'twitch';
  channel: string;
  receivedAt: number;
  // notice
  externalId?: string;
  noticeType?: string;
  systemMsg?: string;
  username?: string | null;
  payload?: Record<string, string>;
  // delete
  targetMessageId?: string;
  // clearchat
  targetUsername?: string | null;
  banDurationSeconds?: number | null;
}

export interface QuotaWarningEvent {
  type: 'quota_warning';
  source: 'youtube';
  percent: number;
}

// ── EventSub Channel Events ──────────────────────────────────────────────────

export interface ChannelFollowEvent {
  type: 'follow';
  source: 'twitch';
  username: string;
  displayName: string;
  followedAt: string;
  receivedAt: number;
}

export interface ChannelSubscribeEvent {
  type: 'subscribe';
  source: 'twitch';
  username: string;
  displayName: string;
  tier: string;
  isGift: boolean;
  receivedAt: number;
}

export interface ChannelResubEvent {
  type: 'resub';
  source: 'twitch';
  username: string;
  displayName: string;
  tier: string;
  cumulativeMonths: number;
  streakMonths: number | null;
  message: string | null;
  receivedAt: number;
}

export interface ChannelGiftSubEvent {
  type: 'gift_sub';
  source: 'twitch';
  username: string;
  displayName: string;
  total: number;
  cumulativeTotal: number | null;
  tier: string;
  isAnonymous: boolean;
  receivedAt: number;
}

export interface ChannelCheerEvent {
  type: 'cheer';
  source: 'twitch';
  username: string;
  displayName: string;
  bits: number;
  message: string;
  isAnonymous: boolean;
  receivedAt: number;
}

export interface ChannelRedemptionEvent {
  type: 'redemption';
  source: 'twitch';
  username: string;
  displayName: string;
  rewardTitle: string;
  rewardCost: number;
  userInput: string;
  receivedAt: number;
}

export interface ChannelRaidEvent {
  type: 'raid';
  source: 'twitch';
  fromUsername: string;
  fromDisplayName: string;
  viewers: number;
  receivedAt: number;
}

export interface ChannelViewerMilestoneEvent {
  type: 'viewer_milestone';
  source: 'twitch';
  username: string;
  displayName: string;
  streakCount: number;
  systemMsg: string;
  /** Optional custom message the viewer typed when sharing their watch streak. */
  message: string;
  receivedAt: number;
}

/**
 * A first-time subscriber's optional shared message (USERNOTICE msg-id=sub with
 * a non-empty trailing chat message). Only emitted when a message is present —
 * the sub itself is already shown via the aggregate EventSub ChannelSubscribeEvent,
 * which has no message field of its own.
 */
export interface ChannelSubShareEvent {
  type: 'sub_share_message';
  source: 'twitch';
  username: string;
  displayName: string;
  message: string;
  receivedAt: number;
}

/** Twitch `/announce` command (USERNOTICE msg-id=announcement). */
export interface ChannelAnnouncementEvent {
  type: 'announcement';
  source: 'twitch';
  username: string;
  displayName: string;
  message: string;
  /** Raw msg-param-color value, lowercased (primary/blue/green/orange/purple), or '' if unset. */
  color: string;
  receivedAt: number;
}

/**
 * A single named recipient of a gifted sub (USERNOTICE msg-id=subgift).
 * Separate from ChannelGiftSubEvent, which only carries the aggregate total
 * from EventSub — this shows the individual "X hat Y ein Abo geschenkt" line.
 */
export interface ChannelSubGiftRecipientEvent {
  type: 'gift_sub_recipient';
  source: 'twitch';
  gifterUsername: string | null;
  gifterDisplayName: string;
  recipientDisplayName: string;
  tier: string;
  isAnonymous: boolean;
  receivedAt: number;
}

export interface ChannelModeChangeEvent {
  type: 'mode_change';
  source: 'twitch';
  modBy: string;
  modes: {
    slow_mode?: boolean;
    slow_mode_wait_time?: number;
    subscriber_mode?: boolean;
    emote_mode?: boolean;
    follower_mode?: boolean;
    follower_mode_duration?: number;
    unique_chat_mode?: boolean;
  };
  receivedAt: number;
}

export function isTwitchChannelEvent(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string; source?: string };
  return v.source === 'twitch' &&
    (v.type === 'follow' || v.type === 'subscribe' || v.type === 'resub' ||
     v.type === 'gift_sub' || v.type === 'gift_sub_recipient' || v.type === 'cheer' || v.type === 'redemption' ||
     v.type === 'raid' || v.type === 'viewer_milestone' || v.type === 'mode_change' ||
     v.type === 'announcement' || v.type === 'sub_share_message');
}

/** Default user color when Twitch tag color is empty. */
export const DEFAULT_USER_COLOR = '#9147ff';

/** Type guard: is this a chat message frame from the backend? */
export function isTwitchMessage(value: unknown): value is TwitchMessage {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string; source?: string; externalId?: string; text?: string };
  return v.type === 'message' && v.source === 'twitch'
    && typeof v.externalId === 'string' && typeof v.text === 'string';
}

/** Type guard: is this a YouTube chat message frame from the backend? */
export function isYouTubeMessage(value: unknown): value is YouTubeMessage {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string; source?: string; externalId?: string; text?: string };
  return (v.type === 'message' || v.type === 'superChat' || v.type === 'newSponsor')
    && v.source === 'youtube'
    && typeof v.externalId === 'string' && typeof v.text === 'string';
}

/** Type guard: is this a non-message chat event (notice/delete/clearchat)? */
export function isTwitchChatEvent(value: unknown): value is TwitchChatEvent {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string; source?: string };
  return (v.type === 'notice' || v.type === 'delete' || v.type === 'clearchat' || v.type === 'unban')
    && v.source === 'twitch';
}

/** Type guard: is this a YouTube quota-warning event? */
export function isQuotaWarningEvent(value: unknown): value is QuotaWarningEvent {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string; source?: string; percent?: unknown };
  return v.type === 'quota_warning' && v.source === 'youtube' && typeof v.percent === 'number';
}
